import React, { useEffect, useState } from 'react';
import { Heart, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * LikeButton — a small heart button children tap to save/unsave content.
 *
 * Props:
 *   childId (required)      — the child taking the action
 *   contentType (required)  — bible_story | ai_story | math_lesson | reading_material | generated_material | worksheet | summer_slide
 *   contentId (required)    — stable string id of the item
 *   metadata (optional)     — { title, subject, band, preview, thumbnail_emoji } stored with the like
 *   size (optional)         — 'sm' | 'md' | 'lg' (default md)
 *   showCount (optional)    — show like count next to the heart (default true)
 *   testIdPrefix (optional) — for data-testid
 */
const SIZE = {
  sm: { icon: 'w-4 h-4', text: 'text-xs', pad: 'p-1' },
  md: { icon: 'w-5 h-5', text: 'text-sm', pad: 'p-1.5' },
  lg: { icon: 'w-6 h-6', text: 'text-base', pad: 'p-2' },
};

const LikeButton = ({
  childId,
  contentType,
  contentId,
  metadata = {},
  size = 'md',
  showCount = true,
  testIdPrefix = 'like',
  onToggle,
}) => {
  const { api } = useAuth();
  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let ignore = false;
    if (!contentId || !contentType) return;
    api
      .get(`/content/likes/${contentType}/${encodeURIComponent(contentId)}?child_id=${encodeURIComponent(childId || '')}`)
      .then((r) => {
        if (ignore) return;
        setLiked(!!r.data.liked_by_me);
        setCount(r.data.likes || 0);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => { ignore = true; };
  }, [api, childId, contentType, contentId]);

  const toggle = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (busy || !childId) return;
    setBusy(true);
    try {
      const res = await api.post('/content/like', {
        child_id: childId,
        content_type: contentType,
        content_id: contentId,
        metadata,
      });
      const nowLiked = !!res.data.liked;
      setLiked(nowLiked);
      setCount((c) => Math.max(0, c + (nowLiked ? 1 : -1)));
      if (onToggle) onToggle({ liked: nowLiked, contentType, contentId });
      if (nowLiked) {
        toast.success('Added to your library');
      }
    } catch {
      toast.error("Couldn't save your like — try again.");
    } finally {
      setBusy(false);
    }
  };

  const sz = SIZE[size] || SIZE.md;
  return (
    <button
      onClick={toggle}
      disabled={busy || !childId}
      className={`inline-flex items-center gap-1 rounded-full ${sz.pad} border transition-all ${
        liked
          ? 'border-rose-300 bg-rose-50 text-rose-600 hover:bg-rose-100'
          : 'border-slate-200 bg-white text-slate-500 hover:border-rose-300 hover:text-rose-500'
      } disabled:opacity-60`}
      title={liked ? 'Remove from library' : 'Add to library'}
      data-testid={`${testIdPrefix}-${contentType}-${contentId}`}
    >
      {busy ? (
        <Loader2 className={`${sz.icon} animate-spin`} />
      ) : (
        <Heart className={`${sz.icon} ${liked ? 'fill-rose-500 text-rose-500' : ''}`} />
      )}
      {showCount && loaded && (
        <span className={`${sz.text} font-semibold tabular-nums`}>{count}</span>
      )}
    </button>
  );
};

export default LikeButton;
